/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

/**
 * Agent Detail (redesigned)
 *
 * Slim orchestrator: owns the top-level queries, mutations, and tab
 * routing, then delegates rendering to focused subcomponents under
 * `./detail/`. The old monolithic implementation lived here at ~1600
 * lines; this file is intentionally much smaller.
 */

import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Paper, Tab, Tabs } from '@mui/material';
import {
  AutoAwesome as OverviewIcon,
  Build as ToolIcon,
  History as HistoryIcon,
  Memory as MemoryIcon,
  SmartToy as AgentIcon,
  Tune as AdvancedIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';

import { agentApi } from '../../services/api';
import { LoadingSpinner, ConfirmDialog } from '../common';
import { useAuth } from '../../context/AuthContext';
import PageBackButton from '../../../components/PageBackButton';

import { AgentSubAgentManager } from './AgentSubAgentManager';
import { AgentHeader } from './detail/AgentHeader';
import { AgentKpiStrip } from './detail/AgentKpiStrip';
import { OverviewTab } from './detail/OverviewTab';
import { ToolsTab } from './detail/ToolsTab';
import { MemoryTab } from './detail/MemoryTab';
import { ExecutionsTab } from './detail/ExecutionsTab';
import { AdvancedTab } from './detail/AdvancedTab';
import { AgentEditDialog, type AgentEditValues } from './detail/AgentEditDialog';
import {
  MemoryConfigDialog,
  type MemoryConfigValues,
} from './detail/MemoryConfigDialog';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) =>
  value === index ? (
    <Box role="tabpanel" sx={{ p: { xs: 1.5, sm: 2, md: 2.5 } }}>
      {children}
    </Box>
  ) : null;

export const AgentDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const agentId = parseInt(id || '0', 10);
  const isStaff = Boolean(user?.is_staff);

  const [activeTab, setActiveTab] = useState(0);
  const [editOpen, setEditOpen] = useState(false);
  const [memoryOpen, setMemoryOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  // ---------------------------------------------------------------- queries
  const { data: agent, isLoading } = useQuery({
    queryKey: ['agent', agentId],
    queryFn: () => agentApi.get(agentId),
    enabled: !!agentId,
  });

  const { data: toolsData } = useQuery({
    queryKey: ['agent-tools', agentId],
    queryFn: () => agentApi.getTools(agentId),
    enabled: !!agentId,
  });

  const { data: executionsData } = useQuery({
    queryKey: ['agent-executions', agentId],
    queryFn: () => agentApi.getExecutions(agentId),
    enabled: !!agentId,
  });

  const { data: memoryStats } = useQuery({
    queryKey: ['agent-memory-stats', agentId],
    queryFn: () => agentApi.getMemoryStats(agentId),
    enabled: !!agentId,
  });

  // --------------------------------------------------------------- mutations
  const updateMutation = useMutation({
    mutationFn: (data: any) => agentApi.update(agentId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agent', agentId] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.detail || 'Failed to update agent');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => agentApi.delete(agentId),
    onSuccess: () => {
      toast.success('Agent deleted');
      navigate('/ai/agents');
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.detail || 'Failed to delete agent');
    },
  });

  const exportMutation = useMutation({
    mutationFn: () =>
      agentApi.exportAgent(agentId, { maskApiKeys: true, includeSubAgents: true }),
    onSuccess: (payload) => {
      try {
        const json = JSON.stringify(payload, null, 2);
        const blob = new Blob([json], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const safeName = (agent?.name || 'agent').replace(/[^a-z0-9-_]+/gi, '_');
        link.download = `${safeName}.agent.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        toast.success('Agent exported');
      } catch {
        toast.error('Failed to save export file');
      }
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.detail || 'Failed to export agent');
    },
  });

  const cloneMutation = useMutation({
    mutationFn: () => agentApi.cloneAgent(agentId),
    onSuccess: (cloned) => {
      toast.success(`Cloned as "${cloned.name}"`);
      queryClient.invalidateQueries({ queryKey: ['agents'] });
      if (cloned?.id) navigate(`/ai/agents/${cloned.id}`);
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.detail || 'Failed to clone agent');
    },
  });

  // ------------------------------------------------------------------ derived
  const kpis = useMemo(() => {
    const assignments = toolsData?.assigned || agent?.tool_assignments || [];
    const enabled = assignments.filter((a: any) => a.is_enabled).length;
    const runs = executionsData?.results || [];
    const completed = runs.filter((r) => r.status === 'COMPLETED').length;
    const successRate = runs.length > 0 ? completed / runs.length : null;
    const lastRun = runs.length > 0 ? runs[0].started_at : null;
    return {
      toolCount: assignments.length,
      enabledToolCount: enabled,
      executionCount: executionsData?.count ?? runs.length,
      successRate,
      memoryCount: memoryStats?.total ?? 0,
      avgImportance: memoryStats?.avg_importance ?? null,
      lastRunAt: lastRun,
    };
  }, [toolsData, agent, executionsData, memoryStats]);

  // ------------------------------------------------------------------ handlers
  const handleEditSubmit = (values: AgentEditValues) => {
    updateMutation.mutate(values, {
      onSuccess: () => {
        toast.success('Agent updated');
        setEditOpen(false);
      },
    });
  };

  const handleMemorySubmit = (values: MemoryConfigValues) => {
    if (!agent) return;
    updateMutation.mutate(
      {
        model_id: agent.model?.id,
        system_prompt: agent.system_prompt,
        config: { ...(agent.config || {}), ...values },
      },
      {
        onSuccess: () => {
          toast.success('Memory settings saved');
          setMemoryOpen(false);
        },
      }
    );
  };

  const handleToggleActive = (next: boolean) => {
    if (!agent) return;
    updateMutation.mutate(
      {
        model_id: agent.model?.id,
        system_prompt: agent.system_prompt,
        is_active: next,
      },
      {
        onSuccess: () => {
          toast.success(next ? 'Agent activated' : 'Agent deactivated');
        },
      }
    );
  };

  // -------------------------------------------------------------------- render
  if (isLoading || !agent) {
    return <LoadingSpinner />;
  }

  return (
    <Box>
      <AgentHeader
        agent={agent}
        isStaff={isStaff}
        onChat={() => navigate(`/ai/agents/${agentId}/chat`)}
        onEdit={() => setEditOpen(true)}
        onClone={() => cloneMutation.mutate()}
        onExport={() => exportMutation.mutate()}
        onDelete={() => setDeleteOpen(true)}
        onToggleActive={handleToggleActive}
        cloning={cloneMutation.isPending}
        exporting={exportMutation.isPending}
        toggling={updateMutation.isPending}
      />

      <AgentKpiStrip
        toolCount={kpis.toolCount}
        enabledToolCount={kpis.enabledToolCount}
        executionCount={kpis.executionCount}
        successRate={kpis.successRate}
        memoryCount={kpis.memoryCount}
        avgImportance={kpis.avgImportance}
        lastRunAt={kpis.lastRunAt}
      />

      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            px: { xs: 1, sm: 2 },
            '& .MuiTab-root': {
              minHeight: 52,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: { xs: '0.78rem', sm: '0.9rem' },
              minWidth: { xs: 'auto', sm: 120 },
            },
          }}
        >
          <Tab icon={<OverviewIcon fontSize="small" />} iconPosition="start" label="Overview" />
          <Tab
            icon={<ToolIcon fontSize="small" />}
            iconPosition="start"
            label={`Tools${kpis.toolCount ? ` · ${kpis.toolCount}` : ''}`}
          />
          <Tab icon={<AgentIcon fontSize="small" />} iconPosition="start" label="Sub-agents" />
          <Tab
            icon={<MemoryIcon fontSize="small" />}
            iconPosition="start"
            label={`Memory${kpis.memoryCount ? ` · ${kpis.memoryCount}` : ''}`}
          />
          <Tab
            icon={<HistoryIcon fontSize="small" />}
            iconPosition="start"
            label={`Executions${kpis.executionCount ? ` · ${kpis.executionCount}` : ''}`}
          />
          <Tab icon={<AdvancedIcon fontSize="small" />} iconPosition="start" label="Advanced" />
        </Tabs>

        <TabPanel value={activeTab} index={0}>
          <OverviewTab
            agent={agent}
            saving={updateMutation.isPending}
            onOpenFullEdit={() => setEditOpen(true)}
            onSavePartial={async (partial) => {
              await updateMutation.mutateAsync({
                model_id: agent.model?.id,
                system_prompt: agent.system_prompt,
                ...partial,
              });
              toast.success('Agent updated');
            }}
          />
        </TabPanel>
        <TabPanel value={activeTab} index={1}>
          <ToolsTab agentId={agentId} />
        </TabPanel>
        <TabPanel value={activeTab} index={2}>
          <AgentSubAgentManager agentId={agentId} agentName={agent.name} />
        </TabPanel>
        <TabPanel value={activeTab} index={3}>
          <MemoryTab agent={agent} onEditPolicy={() => setMemoryOpen(true)} />
        </TabPanel>
        <TabPanel value={activeTab} index={4}>
          <ExecutionsTab agentId={agentId} />
        </TabPanel>
        <TabPanel value={activeTab} index={5}>
          <AdvancedTab
            agent={agent}
            onDelete={() => setDeleteOpen(true)}
            canDelete={agent.can_edit !== false}
          />
        </TabPanel>
      </Paper>

      <AgentEditDialog
        open={editOpen}
        agent={agent}
        isStaff={isStaff}
        saving={updateMutation.isPending}
        onClose={() => setEditOpen(false)}
        onSubmit={handleEditSubmit}
      />

      <MemoryConfigDialog
        open={memoryOpen}
        agent={agent}
        saving={updateMutation.isPending}
        onClose={() => setMemoryOpen(false)}
        onSubmit={handleMemorySubmit}
      />

      <ConfirmDialog
        open={deleteOpen}
        title="Delete agent"
        message={`Are you sure you want to delete "${agent.name}"? This will also delete all executions and memories. This action cannot be undone.`}
        onConfirm={() => {
          setDeleteOpen(false);
          deleteMutation.mutate();
        }}
        onCancel={() => setDeleteOpen(false)}
        confirmText="Delete"
        confirmColor="error"
      />
    </Box>
  );
};

export default AgentDetail;
